import type {
  BSRow,
  DisplayCurrency,
  Period,
} from "@/hooks/finance/balance-sheet/useBalanceSheet";
import {
  exportStyledExcel,
  periodColLetter,
  type ExportRow,
  type RowKind,
} from "@/components/finance/reports/shared/exportStyledExcel";
import { exportStyledPdf } from "@/components/finance/reports/shared/exportStyledPdf";

export const BS_TITLE = "Balance Sheet";

function kindFor(r: BSRow): RowKind {
  if (r.isHeader) return "header";
  if (r.isCheck) return "check";
  if (r.isTotal) return "total";
  if (r.isSubtotal) return "subtotal";
  return "data";
}

/**
 * Build the shared ExportRow model with live column-relative formulas.
 * Data rows start at excel row 3 (row 1 = title, row 2 = header); the Excel
 * export writes the formulas, the PDF export just renders the values.
 */
export function buildBalanceSheetExportRows(
  rows: BSRow[],
  periods: Period[]
): ExportRow[] {
  const rowNumByKey = new Map<string, number>();
  rows.forEach((r, i) => rowNumByKey.set(r.key, i + 3));
  const ref = (key: string, col: string) => {
    const n = rowNumByKey.get(key);
    return n ? `${col}${n}` : "0";
  };

  // Column-relative formula builders, keyed by row key (mirror the on-screen math).
  const formulas: Record<string, (col: string) => string> = {
    "tot-ca": (c) => `${ref("cash", c)}+${ref("ar", c)}+${ref("inv", c)}`,
    nbv: (c) => `${ref("fa-cost", c)}-${ref("acc-dep", c)}`,
    "tot-nca": (c) => `${ref("nbv", c)}+${ref("other-nca", c)}`,
    "tot-assets": (c) => `${ref("tot-ca", c)}+${ref("tot-nca", c)}`,
    "tot-cl": (c) =>
      `${ref("open-orders", c)}+${ref("deferred", c)}+${ref("ap", c)}+${ref("short-loans", c)}+${ref("accrued", c)}`,
    "tot-ncl": (c) => `${ref("long-loans", c)}+${ref("other-ncl", c)}`,
    "tot-liab": (c) => `${ref("tot-cl", c)}+${ref("tot-ncl", c)}`,
    "tot-equity": (c) => `${ref("tot-assets", c)}-${ref("tot-liab", c)}`,
    "tot-le": (c) => `${ref("tot-liab", c)}+${ref("tot-equity", c)}`,
    check: (c) => `${ref("tot-assets", c)}-${ref("tot-le", c)}`,
  };

  return rows.map((r) => {
    const kind = kindFor(r);
    const builder = formulas[r.key];
    return {
      label: r.label,
      indent: r.indent ?? 0,
      kind,
      cells:
        kind === "header"
          ? undefined
          : periods.map((p, i) => ({
              value: r.values.get(p.key) ?? 0,
              formula: builder ? builder(periodColLetter(i)) : undefined,
            })),
    };
  });
}

/**
 * Export the balance sheet as a clean, gridline-free .xlsx with live
 * SUM/subtraction formulas so the workbook re-computes if a cell is edited.
 */
export function exportBalanceSheetToExcel(
  rows: BSRow[],
  periods: Period[],
  displayCurrency: DisplayCurrency,
  filename: string
) {
  return exportStyledExcel({
    title: BS_TITLE,
    sheetName: `Balance Sheet ${displayCurrency}`,
    periodLabels: periods.map((p) => ({ key: p.key, label: p.label })),
    rows: buildBalanceSheetExportRows(rows, periods),
    displayCurrency,
    fileName: filename,
    currencyDecimals: 0,
    labelColWidth: 40,
  });
}

export function exportBalanceSheetToPdf(
  rows: BSRow[],
  periods: Period[],
  displayCurrency: DisplayCurrency,
  filename: string
) {
  return exportStyledPdf({
    title: BS_TITLE,
    periodLabels: periods.map((p) => ({ key: p.key, label: p.label })),
    rows: buildBalanceSheetExportRows(rows, periods),
    displayCurrency,
    fileName: filename,
    currencyDecimals: 0,
  });
}
